import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { uid } from '../lib/cn'

export type Tema = 'claro' | 'oscuro' | 'sistema'

interface Aviso {
  id: string
  texto: string
  tono: 'exito' | 'error' | 'info'
}

interface EstadoUI {
  tema: Tema
  avisos: Aviso[]
  buscadorAbierto: boolean
  // Las notificaciones (stock bajo, vencimientos, cobros atrasados) se
  // recalculan solas a partir de los datos reales —no son filas que se
  // guarden en la base—, así que "leída" y "eliminada" se llevan acá,
  // por id, guardado en este mismo teléfono. Si mañana la misma alerta
  // vuelve a ser cierta con datos distintos tiene un id nuevo y
  // reaparece; si sigue siendo la misma causa sin resolver, se queda
  // marcada como ya vista.
  notiVistas: string[]
  notiOcultas: string[]
  setTema: (t: Tema) => void
  avisar: (texto: string, tono?: Aviso['tono']) => void
  cerrarAviso: (id: string) => void
  setBuscador: (v: boolean) => void
  marcarNotiVistas: (ids: string[]) => void
  eliminarNotiLeidas: (ids: string[]) => void
}

export const useUI = create<EstadoUI>()(
  persist(
    (set, get) => ({
      // Claro por defecto para todos: se ve blanco como de día. Sigue
      // siendo ajustable por persona desde Ajustes.
      tema: 'claro',
      avisos: [],
      buscadorAbierto: false,
      notiVistas: [],
      notiOcultas: [],

      setTema(tema) {
        set({ tema })
        aplicarTema(tema)
      },

      avisar(texto, tono = 'exito') {
        const aviso = { id: uid('a'), texto, tono }
        set((s) => ({ avisos: [...s.avisos, aviso] }))
        setTimeout(() => get().cerrarAviso(aviso.id), 3800)
      },

      cerrarAviso(id) {
        set((s) => ({ avisos: s.avisos.filter((a) => a.id !== id) }))
      },

      setBuscador(v) {
        set({ buscadorAbierto: v })
      },

      marcarNotiVistas(ids) {
        if (!ids.length) return
        set((s) => ({ notiVistas: [...new Set([...s.notiVistas, ...ids])] }))
      },

      eliminarNotiLeidas(ids) {
        if (!ids.length) return
        set((s) => ({ notiOcultas: [...new Set([...s.notiOcultas, ...ids])] }))
        get().avisar(ids.length === 1 ? 'Notificación eliminada' : 'Notificaciones eliminadas')
      },
    }),
    {
      name: 'novaberry-ui',
      partialize: (s) => ({ tema: s.tema, notiVistas: s.notiVistas, notiOcultas: s.notiOcultas }),
      // v1: se volvió a claro por defecto para todos —incluyendo quienes
      // ya tenían oscuro guardado de la decisión anterior—, así que se
      // fuerza una vez al pasar de la versión sin marcar (v0) a esta.
      version: 1,
      migrate: (persistido) => ({
        tema: 'claro' as Tema,
        notiVistas: (persistido as Partial<EstadoUI>)?.notiVistas ?? [],
        notiOcultas: (persistido as Partial<EstadoUI>)?.notiOcultas ?? [],
      }),
    },
  ),
)

export function aplicarTema(tema: Tema) {
  const oscuro =
    tema === 'oscuro' ||
    (tema === 'sistema' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  document.documentElement.classList.toggle('dark', oscuro)
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', oscuro ? '#0d110d' : '#1B5E37')
}

/** Mantiene el tema "sistema" sincronizado si el usuario cambia el
 *  modo del teléfono con la app abierta. */
export function escucharTemaSistema() {
  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  const handler = () => {
    if (useUI.getState().tema === 'sistema') aplicarTema('sistema')
  }
  mq.addEventListener('change', handler)
  return () => mq.removeEventListener('change', handler)
}
